import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import React, { useState, useEffect } from 'react'

import { CheckboxGroup, ColorFilterGroup, DropdownV2, RadioButtonGroup } from '@src/quince-components/src/atoms'

import { FILTER_DROPDOWN_TYPES, SORT_OPTIONS_CLP_EXPERIMENT } from '../constants/constants'
import { CheckboxOption } from '../menu/filterSection.interface'
import desktopStyles from '../menu/filterSectionWithDrawer/filterSectionWithDrawer.module.scss' // Desktop styles
import styles from '../menu/mobileFilters/mobileFilters.module.scss' // Base styles

const FILTER_DROPDOWN_KEYS = {
  CHECKBOX_FILTER: 'checkbox-filter',
  COLORS_FILTER: 'colors-filter',
  SORT_OPTIONS: 'sort-options',
} as const

const SORT_RADIO_NAME = 'sort-options'

interface FilterDropdownProps {
  type: FILTER_DROPDOWN_TYPES
  label: React.ReactNode
  isActive?: boolean
  options?: any[]
  selectedValues?: string[]
  value?: string
  onTriggerClick?: (e: React.MouseEvent, isOpen: boolean) => void
  onChange?: (value: any) => void
  placement?: 'bottomLeft' | 'bottom' | 'bottomRight'
  buttonClassName?: string
  menuClassName?: string
  items?: any[]
  className?: string
  isMobile?: boolean
  dropdownKey?: string
  forceClose?: boolean
}

export const FilterDropdown: React.FC<FilterDropdownProps> = ({
  type,
  label,
  isActive = false,
  options = [],
  selectedValues = [],
  value = '',
  onTriggerClick,
  onChange,
  placement = 'bottomLeft',
  buttonClassName,
  menuClassName,
  items = [],
  className,
  isMobile = true,
  dropdownKey,
  forceClose = false,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslation('common')

  // Effect to handle force close
  useEffect(() => {
    if (forceClose && isOpen) {
      setIsOpen(false)
    }
  }, [forceClose])

  // Handler for checkbox options
  const handleCheckboxChange = (selectedOptions: CheckboxOption[]) => {
    if (onChange) {
      onChange(selectedOptions)
    }
  }

  // Handler for color selection
  const handleColorChange = (value: string) => {
    if (onChange) {
      onChange(value)
    }
  }

  // Handler for sort selection
  const handleSortChange = (value: string) => {
    if (onChange) {
      onChange(value)
      setIsOpen(false)
    }
  }

  // Handle trigger click with open state management
  const handleTriggerClick = (e: React.MouseEvent, open: boolean) => {
    setIsOpen(open)
    if (onTriggerClick) {
      onTriggerClick(e, open)
    }
  }

  // Use appropriate styles based on viewport
  const styleSet = isMobile ? styles : desktopStyles

  // Conditional rendering based on filter type
  const renderDropdownContent = () => {
    switch (type) {
      case FILTER_DROPDOWN_TYPES.CHECKBOX:
        return {
          items: [
            {
              key: FILTER_DROPDOWN_KEYS.CHECKBOX_FILTER,
              label: (
                <CheckboxGroup
                  options={options as CheckboxOption[]}
                  onChange={handleCheckboxChange}
                  direction="column"
                  maxVisible={8}
                />
              ),
              onClick: undefined,
            },
          ],
          onClick: undefined,
          className: menuClassName,
        }

      case FILTER_DROPDOWN_TYPES.COLOR:
        return {
          items: [
            {
              key: FILTER_DROPDOWN_KEYS.COLORS_FILTER,
              label: (
                <ColorFilterGroup
                  options={options}
                  selectedValues={selectedValues}
                  onChange={handleColorChange}
                  direction="horizontal"
                  circleClassName={styleSet.colorFilterCircle}
                  className={styleSet.colorFilterGroup}
                />
              ),
            },
          ],
          className: menuClassName,
        }

      case FILTER_DROPDOWN_TYPES.SORT:
        return {
          items: [
            {
              key: FILTER_DROPDOWN_KEYS.SORT_OPTIONS,
              label: (
                <RadioButtonGroup
                  name={SORT_RADIO_NAME}
                  options={SORT_OPTIONS_CLP_EXPERIMENT.map(option => ({
                    value: option.value,
                    label: t(option.labelKey),
                  }))}
                  value={value}
                  onChange={handleSortChange}
                  className={styleSet.sortRadioGroup}
                  itemsClassName={styleSet.sortRadioItems}
                  radioItemClassName={styleSet.sortRadioItem}
                  radioLabelClassName={styleSet.sortRadioLabel}
                />
              ),
            },
          ],
          className: menuClassName ?? styleSet.sortDropdownMenu,
        }

      case FILTER_DROPDOWN_TYPES.CARD:
        return {
          items,
          className: menuClassName,
        }

      default:
        return {
          items: [],
          className: menuClassName,
        }
    }
  }
  return (
    <DropdownV2
      menu={renderDropdownContent()}
      trigger={['click']}
      placement={placement}
      autoAdjustOverflow={false}
      isActive={isActive}
      multiSelect={type === 'checkbox' || type === 'color'}
      buttonClassName={classNames(
        isMobile ? styles.mobileFilterButton : desktopStyles.mobileFilterButton,
        buttonClassName,
      )}
      onTriggerClick={handleTriggerClick}
      className={className}
      open={isOpen}
      onOpenChange={open => {
        setIsOpen(open)
      }}
      dropdownKey={dropdownKey}
    >
      {label}
    </DropdownV2>
  )
}
